import { ColoursObj, Typography } from '@eventr/common-storybook'
import { Divider, Stack } from '@mui/material'
import { useSignal } from '@preact/signals-react'
import { CSSProperties, ReactNode } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { Expand } from '../home/components/expand'
import { useBoundStore } from '../state/bound.store'
import { useScrollContext } from '../state/scrollState/scrollContext'
import { es } from '../state/useEphemeralStore'
import { windowSizes } from '../state/windowState/windowSignals'
import { useOpenDisclosures } from '../v2/hooks/useOpenDisclosures'
import { AccordionTransition } from './AccordionTransition'
import { CountLabel } from './CountLabel'
export interface IDisclosureGroupProps {
    // The title of the drawer.
    title: string

    headerTextColour?: string
    decourColour?: string

    // The icon of the drawer.
    icon?: ReactNode

    // The count to display on the right-hand side of the drawer.
    count?: number

    // The content of the drawer. These views are only visible when the drawer is open.
    content?: ReactNode

    // If true, the drawer is initialised in its open state.
    isOpen?: boolean
    countBgColor?: string
    childrenHandlePadding?: boolean
    containerStyle?: CSSProperties
    stickyHeader?: boolean
    borderPosition?: 'bootom' | 'underTitle'
    id?: string
}

export const DisclosureGroup = ({
    title,
    icon,
    count,
    content,
    isOpen,
    headerTextColour = ColoursObj.core.primary,
    decourColour = ColoursObj.core.primary,
    childrenHandlePadding,
    containerStyle,
    stickyHeader,
    countBgColor,
    borderPosition = 'bootom',
    id,
}: IDisclosureGroupProps) => {
    const { headerHeight } = useScrollContext(
        useShallow((s) => ({ headerHeight: s.headerHeight }))
    )
    const { openDisclosures, closedDiscolures } = useOpenDisclosures()
    const isInOpen = openDisclosures?.includes(id ?? '') ?? false
    const isInClosed = closedDiscolures?.includes(id ?? '') ?? false
    const _state = isInOpen ? true : isInClosed ? false : isOpen ?? false
    const openSignal = useSignal<boolean>(_state)

    const override = useBoundStore((s) => s.override)

    const handleClick = () => {
        openSignal.value = !openSignal.value
        if (id)
            es().setDisclosureState(
                window.location.pathname,
                id,
                openSignal.value
            )
    }

    return (
        <Stack
            borderBottom={
                borderPosition === 'bootom'
                    ? '1px solid ' + ColoursObj.shades.shade2
                    : 'none'
            }
            padding={childrenHandlePadding ? '0px' : '8px'}
            style={containerStyle}
        >
            {/* The label of the drawer. The label is always displayed, irrelevant of whether the drawer is open. The label is decorated with an arrow flip indicator. The label of the drawer also acts as the hitbox for opening and closing the drawer. */}
            <Stack
                direction="row"
                justifyContent="space-between"
                onClick={handleClick}
                padding={childrenHandlePadding ? '16px 8px' : '8px 0px'}
                style={{
                    position: stickyHeader ? 'sticky' : 'relative',
                    top:
                        !override && stickyHeader
                            ? headerHeight +
                              (windowSizes.value.insets?.insets.top ?? 0)
                            : 0,
                    zIndex: 1,
                    background: stickyHeader ? 'inherit' : 'transparent',
                    transition: 'top 0.5s ease',
                }}
            >
                <Stack direction="row" spacing="16px">
                    {icon}
                    <Typography
                        variant="bodyBold"
                        color={headerTextColour}
                        id={`disclosureGroupTitleSection${title}Id`.replace(
                            /\s+/g,
                            ''
                        )}
                    >
                        {title}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing="8px">
                    {(count ?? 0) > 0 && (
                        <CountLabel
                            id={title.toLowerCase().replace(/\s+/g, '')}
                            text={count}
                            bgColor={countBgColor}
                        />
                    )}

                    {/* Presents an arrow flip indicator only if content is defined. */}
                    {content && (
                        <Expand
                            expanded={openSignal.value}
                            colour={decourColour}
                        />
                    )}
                </Stack>
            </Stack>
            {borderPosition === 'underTitle' && (
                <Divider color={decourColour} />
            )}
            <AccordionTransition open={openSignal.value}>
                <Stack marginBottom={'8px'}>{content}</Stack>
            </AccordionTransition>
        </Stack>
    )
}
